import { Injectable, Injector, ComponentRef, Type } from '@angular/core';
import { Overlay, OverlayRef, GlobalPositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InjectionToken } from '@angular/core';

export type SidePanelSides = 'left' | 'right';

export interface SidePanelConfig<T = any> {
	side?: SidePanelSides;
	width?: string;
	hasBackdrop?: boolean;
	data?: T;
	minWidth?: string;
}

export const SIDE_PANEL_DATA = new InjectionToken<any>('SIDE_PANEL_DATA');

@Injectable()
export class PhSidePanelService {

	private overlayRef?: OverlayRef;
	private currentSide: SidePanelSides = 'right';
	private backdropClickSubscription?: Subscription;
	private escapeKeySubscription?: Subscription;

	constructor(
		private overlay: Overlay,
		private injector: Injector
	) {}

	open<T>(component: Type<T>, config?: SidePanelConfig): ComponentRef<T> {
		this.close();

		this.currentSide = config?.side ?? 'right';
		const width: string = config?.width ?? '500px';
		const hasBackdrop: boolean = config?.hasBackdrop ?? false;
		const minWidth: string = config?.minWidth ?? '500px';

		const positionStrategy: GlobalPositionStrategy =
			this.overlay
				.position()
				.global()
				.top('0')
				.bottom('0')
				[this.currentSide]('0');

		this.overlayRef = this.overlay.create({
			positionStrategy: positionStrategy,
			width: width,
			height: '100vh',
			minWidth: minWidth,
			hasBackdrop: hasBackdrop,
			backdropClass: 'ph-side-panel-backdrop',
			panelClass: `ph-side-panel-${this.currentSide}`,
			scrollStrategy: this.overlay.scrollStrategies.block(),
			disposeOnNavigation: true
		});

		const injector: Injector = this.createInjector(config?.data);

		const portal = new ComponentPortal(component, null, injector);
		const componentRef: ComponentRef<any> = this.overlayRef.attach(portal);

		if (hasBackdrop) {
			this.backdropClickSubscription = this.overlayRef.backdropClick().subscribe(() => this.close());
		}

		this.escapeKeySubscription = fromEvent<KeyboardEvent>(document, 'keydown')
			.pipe(filter(event => event.key === 'Escape'))
			.subscribe((event: KeyboardEvent) => {
				event.stopPropagation();
				this.close();
			});

		return componentRef;
	}

	close(): void {
		const overlayRef = this.overlayRef;
		if (!overlayRef) return;

		this.backdropClickSubscription?.unsubscribe();
		this.backdropClickSubscription = undefined;
		this.escapeKeySubscription?.unsubscribe();
		this.escapeKeySubscription = undefined;
		this.overlayRef = undefined;

		overlayRef.detachBackdrop();
		overlayRef.addPanelClass(`ph-side-panel-${this.currentSide}-exit`);
		setTimeout(() => overlayRef.dispose(), 300);
	}

	private createInjector(data: any): Injector {
		return Injector.create({
			providers: [
				{ provide: SIDE_PANEL_DATA, useValue: data }
			],
			parent: this.injector
		});
	}
}